import Api from '../../services/Api/Api';
import {
  PaymentCycle_BIMap,
  SelectPlanClickSource,
} from '../../services/BiLogger/BiLoggerTypes';
import { AdvancedPlansProductData, ProductData } from '../../types';
import { Experiments, PaymentCycle, ProductChangeType } from 'enums';
import { AppProps } from './App';
import { STEP_ACTIONS } from 'stores/App.store';

export class AppController {
  constructor(private readonly props: AppProps) {
    const { appContext, setCouponCode } = this.props.rootStore.appStore;
    const { couponCode } = appContext.requestParamsMap;

    if (couponCode) {
      setCouponCode(couponCode);
    }

    if (this.isCycleUpsaleDeepLinkValid()) {
      this.executeCycleUpsaleDeepLink();
      return;
    }

    if (this.isValidProductFromUrl()) {
      this.handelPreSelectedProduct();
    }
  }

  private isCycleUpsaleDeepLinkValid = (): boolean => {
    const { appContext, isAnonymous } = this.props.rootStore.appStore;
    const {
      siteGuid,
      requestParamsMap,
      currentPlanDetails,
      advancedPlansProducts,
      experiments,
    } = appContext;
    const { referralAdditionalInfo, paymentCycle } = requestParamsMap;

    const isCycleUpsaleDeepLinkEnabled =
      experiments[Experiments.CycleUpsaleDeepLink] === 'true';
    const isCycleUpsaleIntent =
      referralAdditionalInfo === 'cycle-upsale-intent' &&
      !!paymentCycle &&
      !!PaymentCycle[paymentCycle];

    if (
      !isCycleUpsaleDeepLinkEnabled ||
      !isCycleUpsaleIntent ||
      isAnonymous ||
      !siteGuid
    ) {
      return false;
    }

    if (
      !currentPlanDetails?.paymentCycle ||
      !currentPlanDetails?.productFamilyId
    ) {
      return false;
    }

    const cycleDurationMonths: Record<PaymentCycle, number> = {
      [PaymentCycle.MONTHLY]: 1,
      [PaymentCycle.QUARTERLY]: 3,
      [PaymentCycle.YEARLY]: 12,
      [PaymentCycle.YEAR2]: 24,
      [PaymentCycle.YEAR3]: 36,
      [PaymentCycle.YEAR4]: 48,
      [PaymentCycle.YEAR5]: 60,
    };

    const targetCycle = PaymentCycle[paymentCycle];
    const currentCycleDuration =
      cycleDurationMonths[currentPlanDetails.paymentCycle] ?? 0;
    const targetCycleDuration = cycleDurationMonths[targetCycle] ?? 0;

    if (targetCycleDuration <= currentCycleDuration) {
      return false;
    }

    const product = advancedPlansProducts.find(
      (p) => p.productFamilyId === currentPlanDetails.productFamilyId,
    );

    return !!product;
  };

  private executeCycleUpsaleDeepLink = (): void => {
    const {
      appContext,
      setCurrentProduct,
      setCurrentCycle,
      activateDirectChecktOut,
    } = this.props.rootStore.appStore;
    const { requestParamsMap, currentPlanDetails, advancedPlansProducts } =
      appContext;
    const { paymentCycle } = requestParamsMap;

    const product = advancedPlansProducts.find(
      (p) => p.productFamilyId === currentPlanDetails?.productFamilyId,
    );

    if (!product) {
      return;
    }

    setCurrentProduct(product);
    setCurrentCycle({ cycle: PaymentCycle[paymentCycle] });
    activateDirectChecktOut();
  };

  isValidProductFromUrl = () => {
    const { products, advancedPlansProducts, requestParamsMap, experiments } =
      this.props.rootStore.appStore.appContext;
    const { productId } = requestParamsMap;

    if (
      experiments[Experiments.DomainUpSell] === 'true' &&
      Boolean(productId)
    ) {
      return true;
    }

    return (
      !!productId &&
      (products.length === 1 || advancedPlansProducts.length === 1)
    );
  };

  handelPreSelectedProduct = () => {
    const {
      appContext,
      setCurrentCycle,
      setCurrentProductWithDefaultCycle,
      setStep,
      activateDirectChecktOut,
      isAnonymous,
    } = this.props.rootStore.appStore;
    const {
      siteGuid,
      requestParamsMap,
      isMobile,
      advancedPlansProducts,
      experiments,
    } = appContext;
    const { paymentCycle, productId } = requestParamsMap;

    const product =
      advancedPlansProducts.find((p) => p.guid === productId) ??
      advancedPlansProducts[0];
    setCurrentProductWithDefaultCycle(product);

    const canGoToStep1 = !isAnonymous && (!isMobile || !!siteGuid);
    let canGoToStep2 = !!siteGuid;
    if (canGoToStep1) {
      setStep(1);
      if (paymentCycle && PaymentCycle[paymentCycle]) {
        setCurrentCycle({
          cycle: PaymentCycle[paymentCycle],
        });
      } else {
        canGoToStep2 = false;
      }

      if (canGoToStep2 && experiments[Experiments.DomainUpSell] !== 'true') {
        activateDirectChecktOut();
      }
    }
  };

  onCycleSelect = () => {
    const { changeStep } = this.props.rootStore.appStore;
    changeStep(STEP_ACTIONS.INCREMENT, () => { });
  };

  onSelectProduct = async (
    product: ProductData | AdvancedPlansProductData,
    clickSource?: SelectPlanClickSource,
    useDefaultCycle = true,
  ) => {
    const {
      changeStep,
      setCurrentProductWithDefaultCycle,
      setCurrentProduct,
      currentCycle,
      isChangePlan,
      openChangePlanModal,
      appContext,
      isDayful,
      appFlow,
    } = this.props.rootStore.appStore;
    if (useDefaultCycle) {
      setCurrentProductWithDefaultCycle(product);
    } else {
      setCurrentProduct(product);
    }

    if (
      isChangePlan &&
      product.productChangeType === ProductChangeType.Downgrade
    ) {
      openChangePlanModal();
    } else {
      changeStep(STEP_ACTIONS.INCREMENT, (fromStep, toStep) => {
        const biLoggerParams = {
          ppType: product.type,
          cycle: PaymentCycle_BIMap[currentCycle],
          productId: product.id,
          productGuid: product.guid,
          referralAdditionalInfo: 'select-button',
          packagePickerType: appContext.packagePickerType,
          cartFlow: appFlow,
        };
        if (isDayful) {
          appContext.biLogger.logDayfulCyclesPageViewBiEvent(biLoggerParams);
        } else {
          appContext.biLogger.logSelectPlanBiEvent({
            ...biLoggerParams,
            clickType: clickSource,
          });
        }
      });
    }
  };

  get fetchOrderPageFunction() {
    const { isDynamicOffering } = this.props.rootStore.appStore.appContext;

    return isDynamicOffering
      ? Api.fetchDynmicOrderPageData
      : Api.fetchOrderPageDataV2;
  }
}
